import { useCallback, useEffect, useRef, useState } from 'react';
import { checkOrderExists } from '../../board/services/boardOrderService';

const OS_SELECTION_RE = /^\d{6}$/;
const OS_SELECTION_ROOTS = '.bch-messages, .bch-open-os-guard-alert';

function readSelectionContext() {
  if (typeof window === 'undefined') return { empty: true, candidate: null };
  const sel = window.getSelection?.();
  if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
    return { empty: true, candidate: null };
  }
  const text = String(sel.toString() || '').trim();
  if (!text) return { empty: true, candidate: null };

  const node = sel.anchorNode;
  const el = node?.nodeType === Node.TEXT_NODE ? node.parentElement : node;
  if (!el?.closest?.(OS_SELECTION_ROOTS)) {
    return { empty: true, candidate: null };
  }
  if (!OS_SELECTION_RE.test(text)) {
    return { empty: false, candidate: null };
  }
  return { empty: false, candidate: text };
}

/**
 * Detecta seleção de 6 dígitos na thread do board-chat (ou no aviso de OS em aberto)
 * e valida se é OS existente. Esconde a ação assim que o texto for deselecionado.
 */
export function useBoardChatOsSelection({ enabled = true, conversationId = null } = {}) {
  const [validOsNumber, setValidOsNumber] = useState(null);
  const [validating, setValidating] = useState(false);
  const reqIdRef = useRef(0);
  const timerRef = useRef(null);

  const clearValidOs = useCallback(() => {
    reqIdRef.current += 1;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setValidOsNumber(null);
    setValidating(false);
  }, []);

  useEffect(() => {
    clearValidOs();
  }, [conversationId, clearValidOs]);

  useEffect(() => {
    if (!enabled) {
      clearValidOs();
      return undefined;
    }

    const evaluate = () => {
      const ctx = readSelectionContext();

      if (ctx.empty || !ctx.candidate) {
        clearValidOs();
        return;
      }

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(async () => {
        const reqId = ++reqIdRef.current;
        setValidating(true);
        try {
          const result = await checkOrderExists(ctx.candidate);
          if (reqId !== reqIdRef.current) return;
          if (result.success && result.exists) {
            setValidOsNumber(result.codigo || ctx.candidate);
          } else {
            setValidOsNumber(null);
          }
        } catch {
          if (reqId !== reqIdRef.current) return;
          setValidOsNumber(null);
        } finally {
          if (reqId === reqIdRef.current) setValidating(false);
        }
      }, 220);
    };

    document.addEventListener('selectionchange', evaluate);
    document.addEventListener('mouseup', evaluate);
    return () => {
      document.removeEventListener('selectionchange', evaluate);
      document.removeEventListener('mouseup', evaluate);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [enabled, clearValidOs]);

  return { validOsNumber, validatingOs: validating, clearValidOs };
}
